import React, { useState, useEffect } from 'react';
import config from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const CustomerProfile = () => {
  const { customer } = useAuth();
  const [profile, setProfile] = useState(null);
  const [customJson, setCustomJson] = useState('{}');
  const [apiInfo, setApiInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  // Profile editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Allowed domains state
  const [allowedDomains, setAllowedDomains] = useState('');
  const [savingDomains, setSavingDomains] = useState(false);

  // Key-Value editor state
  const [jsonPairs, setJsonPairs] = useState([{ key: '', value: '' }]);

  useEffect(() => {
    fetchProfile();
    fetchApiInfo();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/api/profile/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditedProfile(data);
        setCustomJson(JSON.stringify(data.custom_json_data || {}, null, 2));
        // Populate key-value pairs
        if (data.custom_json_data && typeof data.custom_json_data === 'object') {
          const pairs = Object.entries(data.custom_json_data).map(([k, v]) => ({
            key: k,
            value: typeof v === 'object' ? JSON.stringify(v) : String(v)
          }));
          if (pairs.length > 0) setJsonPairs(pairs);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/api/profile/api-info', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiInfo(data);
        setAllowedDomains(data.allowed_api_domains || '');
      }
    } catch (error) {
      console.error('Error fetching API info:', error);
    }
  };

  const handleEditProfile = () => {
    setShowPasswordModal(true);
  };

  const verifyPasswordAndEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/auth/verify-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        setIsEditing(true);
        setShowPasswordModal(false);
        setPassword('');
        setPasswordError('');
      } else {
        setPasswordError('Mật khẩu không chính xác');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      setPasswordError('Có lỗi xảy ra khi xác thực mật khẩu');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedProfile(profile);
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/api/profile/update', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedProfile)
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditedProfile(data);
        setIsEditing(false);
        alert('Thông tin đã được cập nhật thành công!');
      } else {
        alert('Có lỗi xảy ra khi cập nhật thông tin');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Có lỗi xảy ra khi lưu thông tin');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileInputChange = (field, value) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Key-Value editor helpers
  const sanitizeKey = (val) => {
    return val
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\u0111/g, 'd')
      .replace(/\u0110/g, 'D')
      .replace(/[^a-zA-Z0-9_]/g, '')
      .toLowerCase();
  };

  const handleKeyChange = (index, val) => {
    const sanitized = sanitizeKey(val);
    const updated = [...jsonPairs];
    updated[index].key = sanitized;
    setJsonPairs(updated);
    syncJsonFromPairs(updated);
  };

  const handleValueChange = (index, val) => {
    const updated = [...jsonPairs];
    updated[index].value = val;
    setJsonPairs(updated);
    syncJsonFromPairs(updated);
  };

  const addPair = () => {
    setJsonPairs([...jsonPairs, { key: '', value: '' }]);
  };

  const removePair = (index) => {
    if (jsonPairs.length === 1) {
      const reset = [{ key: '', value: '' }];
      setJsonPairs(reset);
      setCustomJson('{}');
      return;
    }
    const updated = jsonPairs.filter((_, i) => i !== index);
    setJsonPairs(updated);
    syncJsonFromPairs(updated);
  };

  const syncJsonFromPairs = (pairs) => {
    const obj = {};
    pairs.forEach(({ key, value }) => {
      if (key.trim()) {
        try { obj[key.trim()] = JSON.parse(value); }
        catch { obj[key.trim()] = value; }
      }
    });
    setCustomJson(JSON.stringify(obj, null, 2));
  };

  const handleSaveJson = async () => {
    try {
      // Validate JSON
      const jsonData = JSON.parse(customJson);
      setJsonError('');
      setSaving(true);

      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/api/profile/update-json', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ custom_json_data: jsonData })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, custom_json_data: data.custom_json_data }));
        alert('Dữ liệu JSON đã được cập nhật thành công!');
      } else {
        alert('Có lỗi xảy ra khi cập nhật dữ liệu');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setJsonError('JSON không hợp lệ. Vui lòng kiểm tra lại cú pháp.');
      } else {
        console.error('Error saving JSON:', error);
        alert('Có lỗi xảy ra khi lưu dữ liệu');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomains = async () => {
    try {
      setSavingDomains(true);
      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/api/profile/update-domains', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allowed_api_domains: allowedDomains })
      });

      if (response.ok) {
        alert('Danh sách domain đã được cập nhật!');
      } else {
        alert('Có lỗi xảy ra khi cập nhật domain');
      }
    } catch (error) {
      console.error('Error saving domains:', error);
      alert('Có lỗi xảy ra');
    } finally {
      setSavingDomains(false);
    }
  };

  const handleGenerateApiPassword = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(config.TASKS_API_URL + '/api/profile/generate-api-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiInfo(data);
        setProfile(prev => ({ ...prev, api_password: data.api_password }));
        alert('API Password đã được tạo thành công!');
      } else {
        alert('Có lỗi xảy ra khi tạo API Password');
      }
    } catch (error) {
      console.error('Error generating API password:', error);
      alert('Có lỗi xảy ra');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Đã sao chép vào clipboard!');
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Thông tin cơ bản
            </button>
            <button
              onClick={() => setActiveTab('json')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'json'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Dữ liệu JSON
            </button>
            <button
              onClick={() => setActiveTab('api')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'api'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              API Access
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Thông tin công ty</h2>
                {!isEditing ? (
                  <button
                    onClick={handleEditProfile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Chỉnh sửa
                  </button>
                ) : (
                  <div className="space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tên công ty</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.company_name || ''}
                      onChange={(e) => handleProfileInputChange('company_name', e.target.value)}
                      className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                      {profile?.company_name || 'N/A'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Người liên hệ</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.contact_person || ''}
                      onChange={(e) => handleProfileInputChange('contact_person', e.target.value)}
                      className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                      {profile?.contact_person || 'N/A'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editedProfile?.email || ''}
                      onChange={(e) => handleProfileInputChange('email', e.target.value)}
                      className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                      {profile?.email || 'N/A'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Số điện thoại</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.phone || ''}
                      onChange={(e) => handleProfileInputChange('phone', e.target.value)}
                      className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                      {profile?.phone || 'N/A'}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Website</label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={editedProfile?.website || ''}
                      onChange={(e) => handleProfileInputChange('website', e.target.value)}
                      className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                      {profile?.website || 'N/A'}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Địa chỉ</label>
                  {isEditing ? (
                    <textarea
                      value={editedProfile?.address || ''}
                      onChange={(e) => handleProfileInputChange('address', e.target.value)}
                      rows={3}
                      className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                      {profile?.address || 'N/A'}
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="bg-yellow-50 p-4 rounded-md">
                  <p className="text-yellow-700">
                    <strong>Lưu ý:</strong> Việc thay đổi thông tin công ty sẽ ảnh hưởng đến tất cả các dự án và hợp đồng liên quan. Vui lòng kiểm tra kỹ trước khi lưu.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* JSON Tab */}
          {activeTab === 'json' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Dữ liệu JSON tùy chỉnh</h2>
                <button
                  onClick={handleSaveJson}
                  disabled={saving || jsonError}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dữ liệu JSON (có thể chỉnh sửa)
                </label>

                {/* Key-Value Pairs Editor */}
                <div className="border border-gray-300 rounded-md p-4 mb-4 bg-gray-50">
                  <div className="flex items-center mb-3">
                    <span className="text-gray-500 font-mono text-sm">{'{'}</span>
                  </div>

                  <div className="flex items-center gap-3 mb-2 px-4">
                    <div className="flex-1 text-center text-sm font-semibold text-gray-600">Key</div>
                    <div className="flex-1 text-center text-sm font-semibold text-gray-600">Value</div>
                    <div style={{width: '40px'}}></div>
                  </div>

                  {jsonPairs.map((pair, index) => (
                    <div key={index} className="flex items-center gap-3 mb-2 px-4">
                      <input
                        type="text"
                        value={pair.key}
                        onChange={(e) => handleKeyChange(index, e.target.value)}
                        placeholder="viet_lien_khong_dau"
                        className="flex-1 p-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        value={pair.value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        placeholder="Giá trị"
                        className="flex-1 p-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={() => removePair(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"
                        title="Xóa"
                        style={{width: '40px', textAlign: 'center'}}
                      >
                        &times;
                      </button>
                    </div>
                  ))}

                  <div className="px-4 mt-2">
                    <button
                      onClick={addPair}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Thêm trường
                    </button>
                  </div>

                  <div className="flex items-center mt-3">
                    <span className="text-gray-500 font-mono text-sm">{'}'}</span>
                  </div>
                </div>

                {/* JSON Preview */}
                <label className="block text-sm font-medium text-gray-700 mb-2">JSON</label>
                <textarea
                  value={customJson}
                  readOnly
                  className="w-full h-40 p-3 border border-gray-300 rounded-md font-mono text-sm bg-gray-100 cursor-default"
                  onClick={(e) => e.target.select()}
                />
                {jsonError && (
                  <p className="mt-2 text-sm text-red-600">{jsonError}</p>
                )}
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Hướng dẫn sử dụng:</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Dữ liệu phải là JSON hợp lệ</li>
                  <li>• Có thể chứa bất kỳ thông tin tùy chỉnh nào</li>
                  <li>• Dữ liệu này có thể được truy cập qua API</li>
                  <li>• Ví dụ: {`{"category": "ecommerce", "priority": "high"}`}</li>
                </ul>
              </div>
            </div>
          )}

          {/* API Tab */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">API Access</h2>
                <button
                  onClick={handleGenerateApiPassword}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Đang tạo...' : 'Tạo API Password mới'}
                </button>
              </div>

              {apiInfo && apiInfo.has_api_access ? (
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-md">
                    <h3 className="text-lg font-medium text-green-900 mb-2">API Access đã được kích hoạt</h3>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Password</label>
                    <div className="flex">
                      <input
                        type="text"
                        value={apiInfo.api_password}
                        readOnly
                        className="flex-1 p-3 border border-gray-300 rounded-l-md bg-gray-50 font-mono text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(apiInfo.api_password)}
                        className="px-4 py-3 bg-gray-600 text-white rounded-r-md hover:bg-gray-700"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API URL</label>
                    <div className="flex">
                      <input
                        type="text"
                        value={window.location.origin + apiInfo.api_url}
                        readOnly
                        className="flex-1 p-3 border border-gray-300 rounded-l-md bg-gray-50 font-mono text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(window.location.origin + apiInfo.api_url)}
                        className="px-4 py-3 bg-gray-600 text-white rounded-r-md hover:bg-gray-700"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="font-medium text-gray-900 mb-2">Cách sử dụng API:</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">HTTP Header:</label>
                        <code className="block bg-white p-2 rounded text-sm">
                          X-API-Password: {apiInfo.api_password}
                        </code>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700">cURL Example:</label>
                        <div className="flex">
                          <code className="flex-1 bg-white p-2 rounded-l text-sm break-all">
                            {apiInfo.curl_example}
                          </code>
                          <button
                            onClick={() => copyToClipboard(apiInfo.curl_example)}
                            className="px-2 py-2 bg-gray-600 text-white rounded-r text-xs hover:bg-gray-700"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 p-4 rounded-md">
                  <h3 className="text-lg font-medium text-yellow-900 mb-2">API Access chưa được kích hoạt</h3>
                  <p className="text-yellow-700">
                    Nhấn nút "Tạo API Password mới" để tạo mật khẩu API và kích hoạt quyền truy cập.
                  </p>
                </div>
              )}

              {/* Allowed Domains Config */}
              <div className="border border-gray-200 rounded-md p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Domain được phép truy cập API</h4>
                  <button
                    onClick={handleSaveDomains}
                    disabled={savingDomains}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingDomains ? 'Đang lưu...' : 'Lưu domain'}
                  </button>
                </div>
                <textarea
                  value={allowedDomains}
                  onChange={(e) => setAllowedDomains(e.target.value)}
                  className="w-full h-24 p-3 border border-gray-300 rounded-md font-mono text-sm"
                  placeholder="Nhập domain, phân cách bằng dấu phẩy. Ví dụ:&#10;khi-tinh-thuong-co-mat.smartera.uk, *.smartera.uk, example.com"
                />
                <div className="mt-2 text-xs text-gray-500 space-y-1">
                  <p>• Mỗi domain cách nhau bằng dấu phẩy</p>
                  <p>• Hỗ trợ wildcard: <code className="bg-gray-100 px-1 rounded">*.smartera.uk</code> cho phép tất cả subdomain</p>
                  <p>• Để trống = cho phép tất cả domain truy cập</p>
                  <p>• Gọi API từ server (curl, backend) không bị giới hạn domain</p>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="font-medium text-blue-900 mb-2">Thông tin API:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• API trả về thông tin công ty và dữ liệu JSON tùy chỉnh</li>
                  <li>• Không bao gồm thông tin nhạy cảm như mật khẩu</li>
                  <li>• Sử dụng header X-API-Password để xác thực</li>
                  <li>• API có thể được gọi từ bên ngoài mà không cần đăng nhập</li>
                  <li>• Cấu hình domain để giới hạn website nào được gọi API</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Xác thực mật khẩu</h3>
            <p className="text-sm text-gray-600 mb-4">
              Vui lòng nhập mật khẩu để xác thực danh tính trước khi chỉnh sửa thông tin:
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mật khẩu
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập mật khẩu của bạn..."
                  onKeyPress={(e) => e.key === 'Enter' && verifyPasswordAndEdit()}
                />
                {passwordError && (
                  <p className="mt-2 text-sm text-red-600">{passwordError}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setPasswordError('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Hủy
                </button>
                <button
                  onClick={verifyPasswordAndEdit}
                  disabled={!password}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Xác thực
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;
